<template>
  <div class="chart-wrapper print-chart-wrapper voids-only-mode mt-15">
    <LoadingAnimation v-if="loading" />
    <div class="chart-content" v-else>
      <div class="hour-title">
        <span class="hour-item pt-2 pb-2 active">
          <img class="inline-block" src="@/assets/image/time-icon.png" />
        </span>
        <span
          v-for="hour in hours"
          :key="hour.value"
          class="hour-item pt-2 pb-2"
          :class="hour.active ? 'active' : ''"
          >{{ hour.show ? hour.label : '' }}
        </span>
      </div>
      <div class="bg-line-mask">
        <div
          v-for="hour in hours"
          :key="hour.value + '-verticle-line'"
          class="verticle-line-item"
        >
          <div class="verticle-line" v-if="hour.show"></div>
        </div>
      </div>
      <div>
        <div v-for="(day, index) in chartData" :key="day.Date" class="day-item">
          <div class="day-title">
            <div>Day {{ index + 1 }}</div>
            <div>
              {{
                userLang.indexOf('en') > -1
                  ? moment(day.Date).format('DD MMM YY')
                  : moment(day.Date).format('DD/MM/YY')
              }}
            </div>
          </div>
          <div class="day-icon-content" v-if="day.Data.length">
            <!-- void event in pad -->
            <div
              v-for="dayItem in day.Data"
              :key="dayItem.PadCycleId + dayItem.StartToToday"
            >
              <div
                v-for="(item, index) in dayItem.Voids"
                :key="item.TimeStamp + item.Status"
              >
                <!-- icon -->
                <el-tooltip
                  placement="top"
                  effect="light"
                  popper-class="event-detail-popup"
                >
                  <!-- poper content -->
                  <div slot="content">
                    <div class="event-item">
                      <div
                        class="event-icon left-side"
                        :class="getEventInfo(item).class"
                      >
                        <img
                          v-if="getEventInfo(item).imgSrc"
                          :src="getEventInfo(item).imgSrc"
                        />
                      </div>
                      <div class="inline-block">
                        <div class="status-text secondary-font">
                          {{ getEventInfo(item).text | firstLetterCapitalize }}
                        </div>
                        <div>{{ moment(item.TimeStamp).format('h:mm A') }}</div>
                        <div v-if="item.Duration">
                          <div class="status-text mt-5 secondary-font">
                            {{ $t('text.duration') }}
                          </div>
                          <div>{{ item.Duration | secondFormat }}</div>
                        </div>
                      </div>
                    </div>
                    <div
                      class="event-item"
                      v-for="subEvent in item.SubEvents"
                      :key="subEvent.TimeStamp + subEvent.Status"
                    >
                      <div
                        class="event-icon left-side"
                        :class="getEventInfo(subEvent).class"
                      >
                        <img
                          v-if="getEventInfo(subEvent).imgSrc"
                          :src="getEventInfo(subEvent).imgSrc"
                        />
                      </div>
                      <div class="inline-block">
                        <div class="status-text secondary-font">
                          {{
                            getEventInfo(subEvent).text | firstLetterCapitalize
                          }}
                        </div>
                        <div>
                          {{ moment(subEvent.TimeStamp).format('h:mm A') }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- There are multiple events near this icon, which are displayed in combination -->
                  <!-- only show the top icon and the bottom icon's edge in the chart -->
                  <div
                    v-if="item.SubEvents.length"
                    class="stack-icon-wrapper-in-tooltip"
                    :style="{
                      left: getEventPosition(dayItem, index)
                    }"
                  >
                    <!-- bottom icon's edge -->
                    <div
                      class="event-icon"
                      :class="getEventInfo(item).class"
                    ></div>
                    <!-- top icon -->
                    <div
                      class="event-icon top-icon"
                      :class="
                        getEventInfo(item.SubEvents[item.SubEvents.length - 1])
                          .class
                      "
                    >
                      <img
                        v-if="
                          getEventInfo(
                            item.SubEvents[item.SubEvents.length - 1]
                          ).imgSrc
                        "
                        :src="
                          getEventInfo(
                            item.SubEvents[item.SubEvents.length - 1]
                          ).imgSrc
                        "
                      />
                    </div>
                  </div>
                  <!-- Single event -->
                  <div
                    v-else
                    class="event-icon"
                    :style="{
                      left: getEventPosition(dayItem, index)
                    }"
                    :class="getEventInfo(item).class"
                  >
                    <img
                      v-if="getEventInfo(item).imgSrc"
                      :src="getEventInfo(item).imgSrc"
                    />
                  </div>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { images, voidState, hours } from '@/const.js'
import LoadingAnimation from '@/components/patients/charts/LoadingAnimation'
import { voidConst } from '@/components/patients/charts/smartzEventsChart/SmartzEventConst.js'
export default {
  data() {
    return {
      images,
      voidState,
      moment,
      userLang: navigator.language || navigator.userLanguage,
      hours,
      dayDuration: 86400,
      voidConst
    }
  },
  props: {
    chartData: {
      type: Array,
      require: true
    },
    loading: {
      type: Boolean,
      require: true
    }
  },
  components: {
    LoadingAnimation
  },
  computed: {
    getEventPosition() {
      return (dayItem, index) => {
        let currentEvent = dayItem.Voids[index]
        let left = (currentEvent.StartToToday / this.dayDuration) * 100 + '%'
        return left
      }
    },
    getEventInfo() {
      return event => {
        let info
        let type = event.Status
        switch (type) {
          case this.voidConst.small:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/small-void.png'),
              class: 'void-event',
              text: event.VoidAmount
                ? `${event.VoidAmount} void`
                : this.$t('text.small_void')
            }
            break
          case this.voidConst.medium:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/medium-void.png'),
              class: 'void-event',
              text: event.VoidAmount
                ? `${event.VoidAmount} void`
                : this.$t('text.medium_void')
            }
            break
          case this.voidConst.large:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/large-void.png'),
              class: 'void-event',
              text: event.VoidAmount
                ? `${event.VoidAmount} void`
                : this.$t('text.large_void')
            }
            break
          default:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/small-void.png'),
              class: 'void-event',
              text: this.$t('text.small_void')
            }
        }
        return info
      }
    }
  }
}
</script>
